import type { SVGCommandArcTo, SVGCommandArcToArgs, SVGCommandClose, SVGCommandCloseArgs, SVGCommandCurveTo, SVGCommandCurveToArgs, SVGCommandLineTo, SVGCommandLineToArgs, SVGCommandMoveTo, SVGCommandMoveToArgs, SVGCommandQuadraticCurve, SVGCommandQuadraticCurveArgs, SVGCommandRaw, SVGCommandRawArgs, SVGCommandSmoothCurveTo, SVGCommandSmoothCurveToArgs, SVGCommandSmoothQuadraticCurve, SVGCommandSmoothQuadraticCurveArgs, SVGPathBaseCommand, SVGPathCommand } from "./types";

const base = <T extends Omit<SVGPathCommand, keyof SVGPathBaseCommand>>(cmd: T): T & SVGPathBaseCommand => {
  return {
    ...cmd,
    _cmd_: 1
  }
}

export const svgPathMoveTo = (args: SVGCommandMoveToArgs): SVGCommandMoveTo => {
  return base({
    ...args,
    key: 'M',
  })
}

export const svgPathLineTo = (args: SVGCommandLineToArgs): SVGCommandLineTo => {
  return base({
    ...args,
    key: 'L',
  })
}

export const svgPathCurveTo = (args: SVGCommandCurveToArgs): SVGCommandCurveTo => {
  return base({
    ...args,
    key: 'C'
  })
}

export const svgPathSmoothCurveTo = (args: SVGCommandSmoothCurveToArgs): SVGCommandSmoothCurveTo => {
  return base({
    ...args,
    key: 'S'
  })
}

export const svgPathQuadraticCurve = (args: SVGCommandQuadraticCurveArgs): SVGCommandQuadraticCurve => {
  return base({
    ...args,
    key: 'Q'
  })
}

export const svgPathSmoothQuadraticCurve = (args: SVGCommandSmoothQuadraticCurveArgs): SVGCommandSmoothQuadraticCurve => {
  return base({
    ...args,
    key: 'T'
  })
}

export const svgPathArc = (args: SVGCommandArcToArgs): SVGCommandArcTo => {
  return base({
    ...args,
    key: 'A',
    large: args.large || false,
    sweep: args.sweep || false,
    rotation: args.rotation ?? 0
  });
}

export const svgPathClose = (args: SVGCommandCloseArgs): SVGCommandClose => {
  return base({
    ...args,
    key: 'Z'
  });
}

export const svgPathRaw = (args: SVGCommandRawArgs): SVGCommandRaw => {
  return base({
    ...args,
    key: 'RAW'
  });
}

const processCommand = (command: SVGPathCommand): string => {
  switch (command.key) {
    case 'M': {
      return `M ${command.point.toString(2, " ")}`;
    };
    case 'L': {
      const move = command.moveTo ? `M ${command.moveTo.toString(2, " ")}` : ''; 
      return [move, `L ${command.point.toString(2, " ")}`].join(' ')
    };
    case 'C': {
      const control = command.control.map(it => it.toString(2, " ")).join(' ');
      return `C ${control}, ${command.end.toString(2, " ")}`;
    };
    case 'S': {
      const control = command.control.map(it => it.toString(2, " ")).join(' ');
      return `C ${control}, ${command.end.toString(2, " ")}`;
    };
    case 'Q': {
      const control = command.control.map(it => it.toString(2, " ")).join(' ');
      return `C ${control}, ${command.end.toString(2, " ")}`;
    };
    case 'T': {
      const control = command.control.map(it => it.toString(2, " ")).join('');
      return `C ${control}, ${command.end.toString(2, " ")}`;
    };
    case 'A': {
      const large = Number(command.large);
      const sweep = Number(command.sweep);
      return `A ${command.point.toString(2, " ")} ${command.rotation} ${large} ${sweep} ${command.end.toString(2, " ")}`;
    };
    case 'Z': {
      return 'Z';
    };
    case 'RAW': {
      return command.value;
    }
    default: return '';
  }
}

export const processCommands = (commands: SVGPathCommand[]): string => {
  return commands.map(cmd => processCommand(cmd)).join(' ');
}
